import React from 'react';
import Yup from '~/util/yup';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { FormModal } from '~/shared/forms/FormModal';
import { getPatientAddressesOptions, editPatientAddressesOptions, LocationOfCareTypeEnum, LocationOfCareTypeOptions } from '@generated';
import { useQuery } from '@tanstack/react-query';
import { TextField } from '~/shared/forms/controls/base-fields/TextField';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { PatientContactSelectField } from '../../contacts/controls/PatientContactSelectField';
import { MapPin } from 'lucide-react';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';

export const validationSchema = Yup.object().shape(
	{
		locationOfCareTypeId: Yup.string().label('Location of Care').nullable().required(),
		locationOfCareFacilityId: Yup.string()
			.label('Location of Care Facility')
			.nullable()
			.when('locationOfCareTypeId', ([locationOfCareTypeId], schema) => {
				if (locationOfCareTypeId && Number(locationOfCareTypeId) === 2) {
					return schema.required();
				}
				return schema;
			}),
		locationOfCarePatientContactId: Yup.string()
			.label('Location of Care Contact')
			.nullable()
			.when('locationOfCareTypeId', ([locationOfCareTypeId], schema) => {
				if (locationOfCareTypeId && Number(locationOfCareTypeId) === 3) {
					return schema.required();
				}
				return schema;
			}),
		primaryAddress: Yup.object()
			.nullable()
			.when('primaryAddressSameAsVisitAddress', ([primaryAddressSameAsVisitAddress], schema) => {
				if (primaryAddressSameAsVisitAddress) {
					return schema;
				}
				return schema.address('Primary Address').required();
			}),
		primaryAddressSameAsVisitAddress: Yup.bool().label('Primary Address same as Visit Address'),
		mailingAddressSameAsVisitAddress: Yup.bool().label('Mailing Address same as Visit Address'),
		mailingAddress: Yup.object()
			.nullable()
			.when('mailingAddressSameAsVisitAddress', ([mailingAddressSameAsVisitAddress], schema) => {
				if (mailingAddressSameAsVisitAddress) {
					return schema;
				}
				return schema.address('Mailing Address').required();
			})
	},
	[
		['locationOfCareTypeId', 'locationOfCareFacilityId'],
		['locationOfCareTypeId', 'locationOfCarePatientContactId'],
		['primaryAddress', 'primaryAddressSameAsVisitAddress'],
		['mailingAddress', 'mailingAddressSameAsVisitAddress']
	]
);

export function PatientAddressesFormModal({ stayId, closeModal }: { stayId: string; closeModal: () => void }) {
	const { data: addresses, isLoading } = useQuery(getPatientAddressesOptions(stayId));
	if (isLoading || !addresses) {
		return null;
	}

	const initialValues = {
		locationOfCareTypeId: null,
		primaryAddressSameAsVisitAddress: true,
		mailingAddressSameAsVisitAddress: true,
		locationOfCareFacilityId: null,
		locationOfCareContactId: null,
		mailingAddress: null,
		primaryAddress: null,
		...addresses
	};

	return (
		<FormModal
			title="Edit Patient Demographics"
			initialValues={initialValues}
			saveMutation={{
				...editPatientAddressesOptions,
				invalidateQueryKeys: [getPatientAddressesOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const locationOfCareTypeId = formHelpers.values.locationOfCareTypeId ? Number(formHelpers.values.locationOfCareTypeId) : null;
				const showLocationOfCareFacility = locationOfCareTypeId === LocationOfCareTypeEnum.Facility;
				const showLocationOfCarePatientContact = locationOfCareTypeId === LocationOfCareTypeEnum.ContactsResidence;
				return (
					<div className="grid grid-cols-1 gap-6">
						<div className="grid grid-cols-1">
							<TextField
								label="Visit Address (Display Only)"
								isReadOnly
								value={`${formHelpers.values.visitAddress.addressOne}, ${formHelpers.values.visitAddress.city}, ${formHelpers.values.visitAddress.state} ${formHelpers.values.visitAddress.zipCode}`}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="col-span-1">
								<SelectFormField name="locationOfCareTypeId" options={LocationOfCareTypeOptions} />
							</div>
							{showLocationOfCareFacility && (
								<div className="col-span-1">
									<AutocompleteSearchFormField name="locationOfCareFacilityId" searchType="facility" />
								</div>
							)}
							{showLocationOfCarePatientContact && (
								<div className="col-span-1">
									<PatientContactSelectField
										name="locationOfCarePatientContactId"
										noContactsLabel="No contacts in residence"
										stayId={stayId}
										filterContact={contact => contact.livesWithPatient}
									/>
								</div>
							)}
						</div>
						<div className="col-span-1 space-y-2">
							<div className="flex items-center gap-2 text-gray-700">
								<MapPin className="size-4" />
								<h3 className="font-bold">Additional Addresses</h3>
							</div>
							<div className="grid grid-cols-2 gap-6">
								<div className="col-span-1 flex flex-col gap-4">
									<CheckboxFormField name="primaryAddressSameAsVisitAddress" hideLabel />

									{formHelpers.values.primaryAddressSameAsVisitAddress === false && (
										<div className="p-2 pt-0">
											<AddressFormField name="primaryAddress" />{' '}
										</div>
									)}
								</div>
								<div className="col-span-1 flex flex-col gap-4">
									<CheckboxFormField name="mailingAddressSameAsVisitAddress" hideLabel />
									{formHelpers.values.mailingAddressSameAsVisitAddress === false && (
										<div className="p-2 pt-0">
											<AddressFormField name="mailingAddress" />
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				);
			}}
		</FormModal>
	);
}
